import React, { useEffect, useState } from 'react';
import { DefaultButton, PrimaryButton, Panel, PanelType, Spinner, MessageBar, MessageBarType, Dropdown, TextField } from '@fluentui/react';
import {
  getMaluchReviewSummary, getMaluchReviewQueue, getMaluchKpoRevisions,
  confirmMaluchEmpatia, rejectMaluchCandidate, verifyMaluchStage,
} from '../api/prospectingApi';
import MaluchOperatorFacilities from './MaluchOperatorFacilities';

const FILTERS = [
  { key: 'candidate', text: 'Dopasowania do sprawdzenia' },
  { key: 'contact_now', text: 'Kontakt teraz' },
  { key: 'unmatched', text: 'Niepotwierdzone' },
  { key: 'confirmed', text: 'Powiązane — potwierdzone' },
  { key: 'linked_probable', text: 'Powiązane — prawdopodobne (automatycznie)' },
  { key: 'rejected', text: 'Odrzucone dopasowania' },
  { key: 'all', text: 'Wszystkie finansowane' },
];
const STAGES = [
  { key: 'unknown', text: 'Nie ustalono etapu' },
  { key: 'funding_only', text: 'Tylko potwierdzone finansowanie' },
  { key: 'preparation', text: 'Przygotowanie inwestycji' },
  { key: 'procurement', text: 'Przetarg / wykonawca' },
  { key: 'construction', text: 'Budowa w toku' },
  { key: 'fitout', text: 'Wyposażanie' },
  { key: 'recruitment', text: 'Rekrutacja / przygotowanie otwarcia' },
  { key: 'opened', text: 'Otwarto (potwierdzone)' },
  { key: 'cancelled', text: 'Anulowano (potwierdzone)' },
];
const stageLabel = (stage) => STAGES.find((entry) => entry.key === stage)?.text || 'Etap niepotwierdzony';
const priorityLabel = {
  contact_now: 'Skontaktuj się teraz', plan_contact: 'Zaplanuj kontakt',
  needs_verification: 'Ustal status inwestycji', registry_confirmed: 'Potwierdzona w Empatii',
  registry_linked_probable: 'Powiązana prawdopodobnie z Empatią',
  monitor: 'Monitoruj', not_prospective: 'Nie jest okazją przedotwarciową',
};
const inputStyle = { maxWidth: 520, width: '100%' };

export default function MaluchReviewPanel({ token, open, onDismiss, onChanged, onOpenEntity }) {
  const [filter, setFilter] = useState('candidate');
  const [ownership, setOwnership] = useState('all');
  const [teryt, setTeryt] = useState('');
  const [terytInput, setTerytInput] = useState('');
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [revisionCache, setRevisionCache] = useState({});
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!open || !token) return undefined;
    const abort = new AbortController();
    setBusy(true); setError('');setItems([]);setNextBefore(null);
    Promise.all([
      getMaluchReviewQueue(token, { status: filter, teryt: teryt.trim(), ownership }, abort.signal),
      getMaluchReviewSummary(token, abort.signal),
    ]).then(([page, counts]) => {
      setItems(page.items || []);
      setNextBefore(page.next_before || null);
      setSummary(counts.totals || {});
    }).catch((e) => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (!abort.signal.aborted) setBusy(false); });
    return () => abort.abort();
  }, [open, token, filter, teryt, ownership, generation]);

  async function more() {
    if (!nextBefore || busy) return;
    setBusy(true); setError('');
    try {
      const res = await getMaluchReviewQueue(token, { status: filter, teryt: teryt.trim(), before: nextBefore, ownership });
      setItems((old) => [...old, ...(res.items || [])]);
      setNextBefore(res.next_before || null);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  const updateDraft = (id, field, value) => setDrafts((old) => ({ ...old, [id]: { ...(old[id] || {}), [field]: value } }));
  const value = (item, field) => drafts[item.id]?.[field] ?? item[field] ?? '';
  async function mutate(callback) {
    setBusy(true); setError('');
    try {
      await callback();
      setGeneration((n) => n + 1);
      onChanged?.();
    } catch (e) { setError(e.message || 'Nie udało się zapisać zmian.'); }
    finally { setBusy(false); }
  }
  async function confirm(item) {
    const target = String(value(item, 'candidate_entity_id')).trim();
    if (!target || !window.confirm(`Potwierdź tożsamość: ${item.application_no} → Empatia ${target}. Sprawdź nazwę, adres i typ opieki. Sama gmina lub pojemność nie są dowodem.`)) return;
    await mutate(() => confirmMaluchEmpatia(token, item.id, target));
  }
  async function reject(item) {
    if (!window.confirm(`Czy potwierdzasz, że kandydat dla ${item.application_no} jest inną placówką? Tymczasowy MALUCH+ wróci na mapę i do ARR.`)) return;
    await mutate(() => rejectMaluchCandidate(token, item.id));
  }
  async function verify(item) {
    const stage = value(item, 'verified_stage') || 'unknown';
    const evidence = String(value(item, 'evidence_url')).trim();
    if (!evidence || !/^https:\/\//i.test(evidence)) {
      setError('Do potwierdzenia etapu potrzebny jest link HTTPS do źródła.');return;
    }
    await mutate(() => verifyMaluchStage(token, {
      application_id: item.id,stage,evidence_url: evidence,
      evidence_note: value(item, 'evidence_note'),
      planned_opening_date: value(item, 'planned_opening_date'),
      actual_opening_date: value(item, 'actual_opening_date'),
      contact_after: value(item, 'contact_after'),
    }));
  }
  async function showRevisions(item) {
    setExpanded((old) => old === item.id ? null : item.id);
    if (revisionCache[item.id]) return;
    try {
      const result = await getMaluchKpoRevisions(token, item.id);
      setRevisionCache((old) => ({ ...old, [item.id]: result.items || [] }));
    } catch(e) {setError(e.message);}
  }
  return (
    <Panel isOpen={open} onDismiss={onDismiss} type={PanelType.large} headerText="MALUCH+ — weryfikacja i otwarcia" closeButtonAriaLabel="Zamknij">
      <div style={{ marginBottom: 16 }}>
        <p>Wszystkie nabory w jednej kolejce. Potwierdzenie Empatii usuwa podwójny ARR. Rewizja umowy KPO sama nie dowodzi ukończenia budowy.</p>
        {summary ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 10 }}>
            <span>Finansowane: <strong>{summary.total_awarded}</strong></span>
            <span>Do weryfikacji: <strong>{summary.to_review}</strong></span>
            <span>Empatia: <strong>{summary.linked_empatia}</strong></span>
            <span>Powiązania automatyczne: <strong>{summary.linked_probable || 0}</strong></span>
            <span>Kontakt teraz: <strong>{summary.contact_now}</strong></span>
          </div>
        ) : null}
        <Dropdown label="Kolejka" selectedKey={filter} options={FILTERS} onChange={(_, option) => { setFilter(option.key); setExpanded(null); }} />
        <Dropdown label="Prowadzący MALUCH+" selectedKey={ownership} options={[{key:'all',text:'Wszyscy'},{key:'public',text:'Gminy / JST'},{key:'private',text:'Podmioty prywatne'}]} onChange={(_, option) => { setOwnership(option.key); setExpanded(null); }} />
        <TextField label="Filtr TERYT (2, 4 lub 7 cyfr)" placeholder="Np. 30" value={terytInput}
          onChange={(_, v) => { if (/^\d{0,7}$/.test(v || '')) setTerytInput(v || ''); }} />
        <DefaultButton text="Zastosuj TERYT" style={{ marginTop: 8 }}
          disabled={![0, 2, 4, 7].includes(terytInput.length)} onClick={() => setTeryt(terytInput)} />
      </div>
      {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
      {busy ? <Spinner label="Pobieram lub zapisuję…" /> : null}
      {items.length === 0 && !busy ? <MessageBar>Brak pozycji dla filtra.</MessageBar> : null}
      {items.map((item) => (
        <div key={item.id} style={{ borderTop: '1px solid #ddd', padding: '14px 0' }}>
          <strong>{item.applicant || item.commune} · {item.care_type === 'club' ? 'klub dziecięcy' : 'żłobek'}</strong>
          <div>{item.commune} · TERYT {item.teryt} · nabór {item.source_round} · wniosek {item.application_no}</div>
          <div>Przyznane miejsca: <strong>{item.awarded_places}</strong> · {priorityLabel[item.contact_priority] || 'Weryfikacja'}</div>
          <div>Ministerstwo: {item.ministry_status || 'brak decyzji tekstowej'} · Status dopasowania: {item.match_status}</div>
          <div>Kontakt dodatkowy z wniosku: {item.contact_raw || 'brak w źródle'} · bez zmiany danych Empatii</div>
          <MaluchOperatorFacilities token={token} application={item} onChanged={() => { setGeneration((n) => n + 1); onChanged?.(); }} onOpenEntity={onOpenEntity} />
          {item.match_status === 'linked_probable' ? <div><strong>Powiązanie prawdopodobne:</strong> Empatia {item.matched_entity_id} (nie jest ręcznie potwierdzone)</div> : null}
          {item.candidate_entity_id ? (
            <div style={{ background: '#f5f5f5', padding: 10, marginTop: 8 }}>
              <strong>Możliwa Empatia (niepotwierdzona):</strong> {item.candidate_name || item.candidate_entity_id}
              <div>Rejestr: {item.candidate_registry_id || '—'} · pojemność: {item.candidate_capacity ?? '—'}</div>
              <div>Adres: {item.candidate_address || 'brak'}</div>
              <TextField label="ID docelowej encji Empatii" style={inputStyle}
                value={value(item, 'candidate_entity_id')} onChange={(_, v) => updateDraft(item.id, 'candidate_entity_id', v || '')} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                <PrimaryButton text="Potwierdź Empatię" disabled={busy} onClick={() => confirm(item)} />
                <DefaultButton text="To inna placówka" disabled={busy} onClick={() => reject(item)} />
                {onOpenEntity ? <DefaultButton text="Pokaż kartę Empatii" onClick={() => onOpenEntity(item.candidate_entity_id)} /> : null}
              </div>
            </div>
          ) : null}
          <DefaultButton style={{ marginTop: 10 }} text={expanded === item.id ? 'Zwiń szczegóły' : 'Etap budowy, termin kontaktu i rewizje KPO'} onClick={() => showRevisions(item)} />
          {expanded === item.id ? (
            <div style={{ padding: 12, background: '#f7f7f7', marginTop: 8 }}>
              <Dropdown label="Potwierdzony etap inwestycji" selectedKey={value(item, 'verified_stage') || 'unknown'} options={STAGES}
                onChange={(_, opt) => updateDraft(item.id, 'verified_stage', opt.key)} />
              <TextField label="Planowana data otwarcia (YYYY-MM-DD)" value={value(item, 'planned_opening_date') || ''} onChange={(_, v) => updateDraft(item.id, 'planned_opening_date', v || '')} />
              <TextField label="Faktyczna data otwarcia (tylko dla etapu Otwarto)" value={value(item, 'actual_opening_date') || ''} onChange={(_, v) => updateDraft(item.id, 'actual_opening_date', v || '')} />
              <TextField label="Przypomnienie o kontakcie (YYYY-MM-DD)" value={value(item, 'contact_after') || ''} onChange={(_, v) => updateDraft(item.id, 'contact_after', v || '')} />
              <TextField label="Link HTTPS potwierdzający etap (wymagany)" value={value(item, 'evidence_url') || ''} onChange={(_, v) => updateDraft(item.id, 'evidence_url', v || '')} />
              <TextField label="Notatka / czego dotyczy źródło" multiline rows={2} value={value(item, 'evidence_note') || ''} onChange={(_, v) => updateDraft(item.id, 'evidence_note', v || '')} />
              <PrimaryButton text="Zapisz zweryfikowany etap" disabled={busy} style={{ marginTop: 8 }} onClick={() => verify(item)} />
              <h4>Rewizje KPO — status umowy, nie status budowy</h4>
              {(revisionCache[item.id] || []).length === 0 ? <div>Brak jednoznacznie powiązanej rewizji.</div> : null}
              {(revisionCache[item.id] || []).map((r, i) => <div key={`${r.source_file}-${r.source_row}-${i}`} style={{ marginBottom: 6 }}>
                {r.stage || 'Etap umowy nieopisany'} · {r.source_file} · wiersz {r.source_row}
              </div>)}
            </div>
          ) : null}
        </div>
      ))}
      {nextBefore ? <DefaultButton text="Załaduj kolejne 30" disabled={busy} onClick={more} style={{ margin: '16px 0' }} /> : null}
    </Panel>
  );
}
